import {
    AuthTokens,
    LogoutEvent,
    LogoutOptions,
    OAuthClient,
    OAuthClientOptions,
    TokenResponse,
    TokenResponseWithTokens,
    UserInfoResponse,
} from '@alchemy/auth';
import {IStorage} from '@alchemy/storage';
import {
    hasReportedFailureToMobileApp,
    MobileAppAccessToken,
    MobileAppTimeoutError,
    reportFailureToMobileApp,
    requestMobileAppAccessToken,
    requestMobileAppOrganizationSwitch,
} from './mobileAppBridge';

const noStorage: IStorage = {
    getItem: () => null,
    setItem: () => {},
    removeItem: () => {},
    clear: () => {},
};

// Inside the mobile app, the dashboard borrows the app's access token through the
// bridge and keeps it in memory only. Refresh tokens rotate on every use, so the app
// and its widgets stay their only holder.
export class MobileAppOAuthClient extends OAuthClient<UserInfoResponse> {
    private appTokens: AuthTokens | undefined;
    private pendingAppTokens: Promise<AuthTokens | undefined> | undefined;
    // A switch or a forced refresh in progress: a token asked for meanwhile could be
    // answered from the app's session before the change.
    private pendingChange: Promise<unknown> | undefined;
    // After a logout, as after a failure reported to the app, only a page reload, which
    // the app drives, starts a new session.
    private loggedOut = false;

    constructor(
        options: Omit<
            OAuthClientOptions,
            | 'storage'
            | 'cookiesOptions'
            | 'refreshTokenStorageKey'
            | 'autoRefreshToken'
        >,
    ) {
        super({...options, storage: noStorage, autoRefreshToken: false});
    }

    public getAccessToken(): string | undefined {
        return this.appTokens?.accessToken;
    }

    public getTokenType(): string | undefined {
        return this.appTokens?.tokenType;
    }

    public getTokens(): AuthTokens | undefined {
        return this.appTokens;
    }

    public hasSession(): boolean {
        return !this.isEnded();
    }

    public isAccessTokenValid(): boolean {
        return this.isValidSession(this.appTokens);
    }

    public async initSession(): Promise<AuthTokens | undefined> {
        while (this.pendingChange) {
            await this.pendingChange.catch(() => {});
        }

        if (this.isEnded() || this.isAccessTokenValid()) {
            return this.appTokens;
        }

        this.pendingAppTokens ??= this.borrow(() =>
            requestMobileAppAccessToken(),
        )
            .then(
                ({tokens}) => tokens,
                () => undefined,
            )
            .finally(() => {
                this.pendingAppTokens = undefined;
            });

        return await this.pendingAppTokens;
    }

    // Callers ask for a refresh when the token claims changed server side.
    async getTokenFromRefreshToken(): Promise<TokenResponseWithTokens> {
        return await this.change(() =>
            this.borrow(() => requestMobileAppAccessToken({force: true})),
        );
    }

    // Only reached after a password change: the app's session outlives it, so its token
    // is renewed rather than a session of the dashboard's own being opened.
    async getTokenFromUsernamePassword(
        _username: string,
        _password: string,
    ): Promise<TokenResponseWithTokens> {
        return await this.getTokenFromRefreshToken();
    }

    // The app owns the session, so it switches it, and its widgets follow.
    async getTokenFromCustomGrantType(
        data: Record<string, any> = {},
    ): Promise<TokenResponseWithTokens> {
        if (data.grant_type !== 'switch_organization') {
            throw new Error(
                `Inside the mobile app, the ${data.grant_type} grant is not available`,
            );
        }

        return await this.change(async () => {
            let token: MobileAppAccessToken;
            try {
                token = await requestMobileAppOrganizationSwitch(
                    data.organization_id,
                );
            } catch (e) {
                // Without an answer, the app may still complete the switch: only a reload
                // shows its actual organization. An explicit failure leaves it unchanged.
                if (e instanceof MobileAppTimeoutError) {
                    reportFailureToMobileApp();
                }

                throw e;
            }

            return await this.adopt(token);
        });
    }

    // A pair issued to the dashboard's own client is never kept: the app's session is
    // renewed instead, so that its token carries the claims the pair was issued for.
    public saveTokensFromResponse(res: TokenResponse): AuthTokens {
        return toAuthTokens(res);
    }

    public async triggerLogin(_tokens: AuthTokens): Promise<void> {
        await this.getTokenFromRefreshToken();
    }

    public async logout(
        options: LogoutOptions = {},
    ): Promise<LogoutEvent | undefined> {
        this.appTokens = undefined;
        this.loggedOut = true;

        return await super.logout(options);
    }

    private async borrow(
        request: () => Promise<MobileAppAccessToken>,
    ): Promise<TokenResponseWithTokens> {
        let token: MobileAppAccessToken;
        try {
            token = await request();
        } catch (e) {
            // The app's error screen owns recovery, as for any failed session boot.
            reportFailureToMobileApp();

            throw e;
        }

        return await this.adopt(token);
    }

    private async change<T>(run: () => Promise<T>): Promise<T> {
        const start = () => {
            if (this.isEnded()) {
                throw new Error('The borrowed session has ended');
            }

            return run();
        };

        // One request to the app at a time: each one is answered from the app's session
        // as left by the previous one.
        const previous = (this.pendingChange ?? this.pendingAppTokens)?.catch(
            () => {},
        );
        const p = previous ? previous.then(start) : start();
        this.pendingChange = p;
        try {
            return await p;
        } finally {
            if (this.pendingChange === p) {
                this.pendingChange = undefined;
            }
        }
    }

    private isEnded(): boolean {
        return this.loggedOut || hasReportedFailureToMobileApp();
    }

    private async adopt({
        accessToken,
        expiresIn,
    }: MobileAppAccessToken): Promise<TokenResponseWithTokens> {
        if (this.isEnded()) {
            throw new Error('The borrowed session has ended');
        }

        const now = Math.ceil(new Date().getTime() / 1000);
        const tokens: AuthTokens = {
            tokenType: 'Bearer',
            accessToken,
            expiresIn,
            expiresAt: now + expiresIn,
            refreshToken: '',
            refreshExpiresIn: 0,
            refreshExpiresAt: 0,
        };
        this.appTokens = tokens;
        this.sessionHasExpired = false;
        await super.triggerLogin(tokens);

        return toTokenResponse(tokens);
    }
}

function toAuthTokens(res: TokenResponse): AuthTokens {
    const now = Math.ceil(new Date().getTime() / 1000);

    return {
        tokenType: res.token_type,
        accessToken: res.access_token,
        expiresIn: res.expires_in,
        expiresAt: now + res.expires_in,
        refreshToken: res.refresh_token,
        refreshExpiresIn: res.refresh_expires_in,
        refreshExpiresAt: now + res.refresh_expires_in,
    };
}

function toTokenResponse(tokens: AuthTokens): TokenResponseWithTokens {
    return {
        access_token: tokens.accessToken,
        token_type: tokens.tokenType,
        expires_in: tokens.expiresIn,
        refresh_token: '',
        refresh_expires_in: 0,
        tokens,
    };
}
